import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { courtCasesApi, documentsApi, extractOne } from '../../api/facades';
import { SignatureAuditList } from '../../components/DocumentSigning/SignatureAuditList';

const CourtCaseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = id ? parseInt(id, 10) : NaN;
  const [showDelete, setShowDelete] = React.useState(false);
  const [showUpload, setShowUpload] = React.useState(false);
  const [uploadFile, setUploadFile] = React.useState<File | null>(null);
  const [uploadName, setUploadName] = React.useState('');

  const { data: res, isLoading, isError } = useQuery({
    queryKey: ['court-cases', numId],
    queryFn: () => courtCasesApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const row = extractOne(res);

  const deleteMu = useMutation({
    mutationFn: () => courtCasesApi.remove(numId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['court-cases'] });
      setShowDelete(false);
      navigate('/dashboard/court-cases');
      toast.success('Court case deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const uploadMu = useMutation({
    mutationFn: () => documentsApi.upload(numId, uploadFile!, uploadName.trim() || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['court-cases', numId] });
      setShowUpload(false);
      setUploadFile(null);
      setUploadName('');
      toast.success('Document uploaded.');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e?.message || 'Upload failed.'),
  });

  if (Number.isNaN(numId) || isError || (!isLoading && !row)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-slate-600 dark:text-slate-400">Court case not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/dashboard/court-cases')}>Back</Button>
      </div>
    );
  }

  const r = row as any;

  const relatedDocuments = (r?.documents as any[]) || [];

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200">
          {isLoading ? 'Loading…' : r?.case_number || r?.party_name || 'Court Case'}
        </h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/dashboard/court-cases')}>Back</Button>
          <Button variant="primary" onClick={() => navigate(`/dashboard/court-cases/${id}/edit`)}>Edit</Button>
          <Button variant="secondary" onClick={() => setShowDelete(true)} className="text-red-600 hover:text-red-700">Delete</Button>
        </div>
      </div>
      {isLoading ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><dt className="text-xs font-semibold text-slate-500 uppercase">Party</dt><dd className="text-slate-700 dark:text-slate-200">{r?.party_name ?? '—'}</dd></div>
          <div><dt className="text-xs font-semibold text-slate-500 uppercase">Party lawyer</dt><dd className="text-slate-700 dark:text-slate-200">{r?.party_lawyer ?? '—'}</dd></div>
          <div><dt className="text-xs font-semibold text-slate-500 uppercase">Case status</dt><dd className="text-slate-700 dark:text-slate-200">{r?.case_status?.case_status_name ?? '—'}</dd></div>
          <div><dt className="text-xs font-semibold text-slate-500 uppercase">Judge</dt><dd className="text-slate-700 dark:text-slate-200">{r?.judge?.judge_name ?? '—'}</dd></div>
          <div><dt className="text-xs font-semibold text-slate-500 uppercase">Priority</dt><dd className="text-slate-700 dark:text-slate-200">{r?.priority ?? '—'}</dd></div>
          <div><dt className="text-xs font-semibold text-slate-500 uppercase">Client position</dt><dd className="text-slate-700 dark:text-slate-200">{r?.client_position ?? '—'}</dd></div>
          <div><dt className="text-xs font-semibold text-slate-500 uppercase">Date of service</dt><dd className="text-slate-700 dark:text-slate-200">{r?.date_of_service ? String(r.date_of_service).slice(0, 10) : '—'}</dd></div>
          <div><dt className="text-xs font-semibold text-slate-500 uppercase">Active</dt><dd className="text-slate-700 dark:text-slate-200">{r?.is_active ?? '—'}</dd></div>
          {(r?.remark || r?.description) && (
            <div className="md:col-span-2">
              <dt className="text-xs font-semibold text-slate-500 uppercase">{r?.remark ? 'Remark' : 'Description'}</dt>
              <dd className="text-slate-700 dark:text-slate-200">{r?.remark || r?.description}</dd>
            </div>
          )}
        </dl>
        <div className="mt-6 border-t border-slate-200 dark:border-slate-700 pt-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Case documents</h2>
            <Button variant="primary" onClick={() => setShowUpload(true)} className="text-xs">
              Upload document
            </Button>
          </div>
          {relatedDocuments.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">No documents yet. Upload a file to attach it to this case.</p>
          ) : (
            <div className="space-y-4">
              {relatedDocuments.map((doc) => (
                <div key={doc.id} className="border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div>
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                        {doc.document_name || `Document #${doc.id}`}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Type: {doc.document_type ?? '—'}
                      </p>
                    </div>
                    <Button
                      variant="primary"
                      onClick={() => navigate(`/dashboard/documents/${doc.id}/sign`)}
                      className="text-xs"
                    >
                      View / Sign
                    </Button>
                  </div>
                  <SignatureAuditList documentId={doc.id} signatures={doc.signatures || []} />
                </div>
              ))}
            </div>
          )}
        </div>
        </>
      )}
      <ConfirmDeleteModal open={showDelete} onClose={() => setShowDelete(false)} onConfirm={() => deleteMu.mutate()} title="Delete court case" message="Are you sure? This cannot be undone." loading={deleteMu.isPending} />

      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !uploadMu.isPending && setShowUpload(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-xl max-w-md w-full mx-4 border border-slate-200 dark:border-slate-700" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">Upload document</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">File</label>
                <input
                  type="file"
                  className="w-full text-sm text-slate-600 dark:text-slate-300 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700 dark:file:bg-slate-700 dark:file:text-slate-200"
                  onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Document name (optional)</label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 py-2 px-3 text-sm text-slate-700 dark:text-slate-200"
                  placeholder="Uses file name if blank"
                  value={uploadName}
                  onChange={(e) => setUploadName(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6 justify-end">
              <Button variant="secondary" onClick={() => setShowUpload(false)} disabled={uploadMu.isPending}>Cancel</Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (uploadFile) uploadMu.mutate();
                  else toast.error('Select a file.');
                }}
                disabled={!uploadFile || uploadMu.isPending}
              >
                {uploadMu.isPending ? 'Uploading…' : 'Upload'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourtCaseDetailPage;
